import {
  MotifInput,
  type MotifInput as SegnaviaMotifInput,
} from '@segnavia/format'
import { z } from 'zod'
import type { Format } from './format'

export type StoredMotif = Omit<SegnaviaMotifInput, 'revision'>

/** The stored definition is Segnavia's motif input with its host revision removed. */
const { revision: _revisionInput, ...storedMotifShape } = MotifInput.shape
export const StoredMotifInput = z
  .strictObject(storedMotifShape)
  .superRefine((value, context) => {
    const parsed = MotifInput.safeParse({ ...value, revision: 'stored-body' })
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        context.addIssue({
          code: 'custom',
          message: issue.message,
          path: issue.path,
        })
      }
    }
  })

export function storedMotifResult(
  body: string,
): { ok: true; motif: StoredMotif } | { ok: false; reason: string } {
  let value: unknown
  try {
    value = JSON.parse(body)
  } catch {
    return { ok: false, reason: 'the body must be valid JSON' }
  }
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.hasOwn(value, 'revision')
  ) {
    return {
      ok: false,
      reason: 'the body must be an object and must not store revision',
    }
  }
  const parsed = MotifInput.safeParse({ ...value, revision: 'stored-body' })
  if (!parsed.success) {
    return {
      ok: false,
      reason: parsed.error.issues
        .map((issue) => `${issue.path.join('.') || 'body'}: ${issue.message}`)
        .join('; '),
    }
  }
  const { revision: _revision, ...motif } = parsed.data
  return { ok: true, motif }
}

export function storedMotifOf(body: string): StoredMotif | null {
  const parsed = storedMotifResult(body)
  return parsed.ok ? parsed.motif : null
}

export const motif = {
  bodyStorage: 'inline',
  validate: (body: string) => storedMotifOf(body) !== null,
  outline: () => [],
  searchText: (body: string) => {
    const stored = storedMotifOf(body)
    return stored
      ? [
          stored.key,
          stored.label,
          ...stored.fields.map((field) => field.label),
        ].join(' ')
      : ''
  },
  tools: [],
} as const satisfies Format
